import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, Check, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { confirmGroupResult, subscribeToSession } from "@/lib/groupSession";
import { fetchWatchProviders } from "@/lib/tmdb";
import type { WatchProvider } from "@/lib/tmdb";
import ConfettiEffect from "@/components/ConfettiEffect";
import GuestPostSession from "@/components/GuestPostSession";

interface GroupResultsScreenProps {
  sessionId: string;
  memberId: string;
  winner: any;
  runnersUp: any[];
  isRandom: boolean;
  isGuest: boolean;
  guestDisplayName?: string;
  guestId?: string;
  onClosed: () => void;
  onDone?: () => void;
}

const GroupResultsScreen = ({ sessionId, memberId, winner, runnersUp, isRandom, isGuest, guestDisplayName, guestId, onClosed, onDone }: GroupResultsScreenProps) => {
  const { t } = useTranslation();
  const [confirmed, setConfirmed] = useState(false);
  const [showGuestCta, setShowGuestCta] = useState(false);
  const [loading, setLoading] = useState(false);
  const [providerMap, setProviderMap] = useState<Record<number, WatchProvider[]>>({});
  const [watchLinkMap, setWatchLinkMap] = useState<Record<number, string>>({});
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const channel = subscribeToSession(sessionId, (session) => {
      if (session.status === 'closed') {
        if (isGuest) {
          setShowGuestCta(true);
        } else {
          onClosed();
        }
      }
    }, guestId);
    return () => { channel.unsubscribe(); };
  }, [sessionId, isGuest]);

  useEffect(() => {
    const allItems = [winner, ...runnersUp].filter(Boolean);
    const uniqueIds = [...new Set(allItems.map((item: any) => item.id))];
    if (uniqueIds.length === 0) return;

    const region = localStorage.getItem("ayeris_region") || "GB";
    const mediaType = winner?.media_type || "movie";

    const embedded: Record<number, WatchProvider[]> = {};
    let allHaveProviders = true;
    for (const item of allItems) {
      if (item?.providers && item.providers.length > 0) {
        embedded[item.id] = item.providers;
      } else {
        allHaveProviders = false;
      }
    }
    if (allHaveProviders && Object.keys(embedded).length > 0) {
      setProviderMap(embedded);
      return;
    }

    Promise.all(
      uniqueIds.map(id =>
        fetchWatchProviders(id, mediaType, region)
          .then(result => ({ id, providers: result.providers, link: result.link }))
          .catch(() => ({ id, providers: [] as WatchProvider[], link: null as string | null }))
      )
    ).then(results => {
      const map: Record<number, WatchProvider[]> = {};
      const links: Record<number, string> = {};
      for (const r of results) {
        map[r.id] = r.providers;
        if (r.link) links[r.id] = r.link;
      }
      setProviderMap(map);
      setWatchLinkMap(links);
    });
  }, [winner, runnersUp]);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await confirmGroupResult(memberId, sessionId);
      setConfirmed(true);
      if (isGuest) {
        setTimeout(() => setShowGuestCta(true), 1500);
      } else if (onDone) {
        onDone();
      } else {
        onClosed();
      }
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const scroll = (dir: number) => {
    scrollRef.current?.scrollBy({ left: dir * 160, behavior: "smooth" });
  };

  const allPicked = runnersUp.filter((item: any) => item?.score > 0);

  const renderProviders = (itemId: number) => {
    const providers = providerMap[itemId];
    const watchLink = watchLinkMap[itemId];
    if (!providers || providers.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1 mt-1.5">
        {providers.map((p) => {
          const inner = p.logo_path ? (
            <img src={p.logo_path} alt={p.provider_name} className={`w-5 h-5 rounded ${watchLink ? "hover:scale-110 transition-transform cursor-pointer" : ""}`} />
          ) : (
            <span className="text-[9px] text-muted-foreground px-1 py-0.5 rounded bg-secondary/60">{p.provider_name}</span>
          );
          return watchLink ? (
            <a key={p.provider_id} href={watchLink} target="_blank" rel="noopener noreferrer" title={p.provider_name}>{inner}</a>
          ) : (
            <div key={p.provider_id} title={p.provider_name}>{inner}</div>
          );
        })}
      </div>
    );
  };

  if (showGuestCta) {
    return <GuestPostSession suggestedUsername={guestDisplayName} />;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center px-4 py-4"
    >
      <ConfettiEffect />
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="w-full max-w-sm bg-gradient-card border border-border rounded-2xl p-4 shadow-card space-y-3 max-h-full overflow-hidden flex flex-col"
      >
        <div className="flex-1 min-h-0 overflow-y-auto space-y-3 scrollbar-hide">
          <div className="text-center">
            <Trophy className="w-8 h-8 text-star mx-auto mb-1.5" />
            <h2 className="font-display text-lg font-bold text-foreground">
              {isRandom ? t("groupResults.randomPick") : t("groupResults.groupWinner")}
            </h2>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {isRandom ? t("groupResults.randomDesc") : t("groupResults.groupSpoken")}
            </p>
          </div>

          {winner && (
            <div className="p-3 rounded-xl bg-primary/10 border border-primary/30 space-y-2">
              <div className="flex gap-3">
                {winner.poster && (
                  <img src={winner.poster} alt={winner.title} className="w-20 h-[120px] rounded-lg object-cover shadow-lg shrink-0" />
                )}
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  <h3 className="font-display font-bold text-foreground text-base leading-tight">{winner.title}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {winner.year} • ⭐ {winner.rating}
                    {winner.certification && (
                      <span className="ml-1 inline-flex items-center px-1 py-0.5 rounded bg-muted text-[9px] font-semibold border border-border/50 align-middle">
                        {winner.certification}
                      </span>
                    )}
                  </p>
                  {winner.score && (
                    <p className="text-xs text-primary font-semibold mt-1">🏆 {winner.score} {winner.score === 1 ? t("groupResults.pick") : t("groupResults.picks")}</p>
                  )}
                  {providerMap[winner.id]?.length > 0 && (
                    <div className="mt-2">
                      <p className="text-[9px] text-muted-foreground tracking-wider uppercase mb-0.5">{t("groupResults.streamOn")}</p>
                       <div className="flex flex-wrap gap-1">
                         {providerMap[winner.id].map((p) => {
                           const inner = (
                              <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-secondary/60 ${watchLinkMap[winner.id] ? "hover:bg-secondary hover:ring-1 hover:ring-primary/30 cursor-pointer" : ""}`}>
                                {p.logo_path && <img src={p.logo_path} alt={p.provider_name} className="w-4 h-4 rounded" />}
                                <span className="text-[9px] text-foreground font-medium">{p.provider_name}</span>
                              </div>
                            );
                            return watchLinkMap[winner.id] ? (
                              <a key={p.provider_id} href={watchLinkMap[winner.id]} target="_blank" rel="noopener noreferrer">{inner}</a>
                            ) : (
                              <div key={p.provider_id}>{inner}</div>
                            );
                          })}
                       </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {allPicked.length > 0 && (
            <div>
              <p className="text-[11px] text-muted-foreground tracking-wider mb-1.5">{t("groupResults.allPicks")}</p>
              <div className="relative">
                {allPicked.length > 2 && (
                  <>
                    <button onClick={() => scroll(-1)} className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-secondary/80 backdrop-blur-sm flex items-center justify-center text-foreground shadow-md">
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => scroll(1)} className="absolute -right-2 top-1/2 -translate-y-1/2 z-10 w-6 h-6 rounded-full bg-secondary/80 backdrop-blur-sm flex items-center justify-center text-foreground shadow-md">
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
                <div ref={scrollRef} className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 px-1" style={{ scrollSnapType: "x mandatory" }}>
                  {allPicked.map((item: any, i: number) => (
                    <div key={item.id || i} className="flex-shrink-0 w-[100px] rounded-lg bg-secondary/50 p-1.5 text-center" style={{ scrollSnapAlign: "start" }}>
                      {item.poster && (
                        <img src={item.poster} alt={item.title} className="w-full h-[110px] rounded object-cover mb-1" />
                      )}
                      <p className="text-[10px] text-foreground font-medium truncate">{item.title}</p>
                      <p className="text-[9px] text-muted-foreground mt-0.5">
                        {item.year}
                        {item.certification && (
                          <span className="ml-0.5 inline-flex items-center px-1 py-0.5 rounded bg-muted text-[8px] font-semibold border border-border/50 align-middle">
                            {item.certification}
                          </span>
                        )}
                      </p>
                      <p className="text-[9px] text-primary font-semibold mt-0.5">
                        {item.score} {item.score === 1 ? t("groupResults.pick") : t("groupResults.picks")}
                      </p>
                      {renderProviders(item.id)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="shrink-0 pt-1">
          {!confirmed ? (
            <button
              onClick={handleConfirm}
              disabled={loading}
              className="w-full py-2.5 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4" /> {t("groupResults.looksGood")}</>}
            </button>
          ) : (
            <div className="flex items-center justify-center gap-2 text-primary py-2.5">
              <Check className="w-4 h-4" />
              <span className="text-sm font-medium">{t("groupResults.confirmed")}</span>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default GroupResultsScreen;
